import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Hook for performance optimization utilities
 * Requirements: 11.1, 11.2
 */
export const usePerformance = () => {
  const [isLowEndDevice, setIsLowEndDevice] = useState(false);
  const [connectionType, setConnectionType] = useState<string>('unknown');

  useEffect(() => {
    // Detect device capabilities
    const detectDeviceCapabilities = () => {
      // Check memory (if available)
      const memory = (navigator as any).deviceMemory;
      if (memory && memory < 4) {
        setIsLowEndDevice(true);
      }

      // Check CPU cores (if available)
      const cores = navigator.hardwareConcurrency;
      if (cores && cores < 4) {
        setIsLowEndDevice(true);
      }

      // Check connection type
      const connection = (navigator as any).connection;
      if (connection) {
        setConnectionType(connection.effectiveType || 'unknown');
      }
    };

    detectDeviceCapabilities();
  }, []);

  return {
    isLowEndDevice,
    connectionType,
    isSlowConnection: connectionType === 'slow-2g' || connectionType === '2g'
  };
};

/**
 * Hook for debouncing values to improve performance
 */
export const useDebounce = <T>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

/**
 * Hook for throttling function calls
 */
export const useThrottle = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): T => {
  const lastRun = useRef(Date.now());

  return useCallback(
    ((...args) => {
      if (Date.now() - lastRun.current >= delay) {
        func(...args);
        lastRun.current = Date.now();
      }
    }) as T,
    [func, delay]
  );
};

/**
 * Hook for intersection observer (lazy loading)
 */
export const useIntersectionObserver = (
  options: IntersectionObserverInit = {}
) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [hasIntersected, setHasIntersected] = useState(false);
  const targetRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const target = targetRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting);
        if (entry.isIntersecting && !hasIntersected) {
          setHasIntersected(true);
        }
      },
      {
        threshold: 0.1,
        ...options
      }
    );

    observer.observe(target);

    return () => {
      observer.unobserve(target);
    };
  }, [hasIntersected, options]);

  return {
    targetRef,
    isIntersecting,
    hasIntersected
  };
};

/**
 * Hook for virtual scrolling (for large lists)
 */
export const useVirtualScroll = <T>(
  items: T[],
  itemHeight: number,
  containerHeight: number
) => {
  const [scrollTop, setScrollTop] = useState(0);

  const visibleItems = useMemo(() => {
    const startIndex = Math.floor(scrollTop / itemHeight);
    const endIndex = Math.min(
      startIndex + Math.ceil(containerHeight / itemHeight) + 1,
      items.length
    );

    return {
      startIndex,
      endIndex,
      items: items.slice(startIndex, endIndex),
      totalHeight: items.length * itemHeight,
      offsetY: startIndex * itemHeight
    };
  }, [items, itemHeight, containerHeight, scrollTop]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(e.currentTarget.scrollTop);
  }, []);

  return {
    visibleItems,
    handleScroll
  };
};

/**
 * Hook for memoizing expensive calculations
 */
export const useExpensiveCalculation = <T, Args extends any[]>(
  calculation: (...args: Args) => T,
  dependencies: Args
): T => {
  return useMemo(() => calculation(...dependencies), dependencies);
};

/**
 * Hook for managing component mount state (prevents memory leaks)
 */
export const useIsMounted = () => {
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return useCallback(() => isMountedRef.current, []);
};

/**
 * Hook for optimized image loading
 */
export const useOptimizedImage = (src: string) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const { isSlowConnection } = usePerformance();

  useEffect(() => {
    if (!src) return;

    const img = new Image();
    
    img.onload = () => setIsLoaded(true);
    img.onerror = () => setHasError(true);
    
    // Load lower quality on slow connections
    img.src = isSlowConnection ? src.replace(/\.(jpg|jpeg|png)$/, '_low.$1') : src;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [src, isSlowConnection]);

  return { isLoaded, hasError };
};

export default usePerformance;